import { gql, useQuery } from '@apollo/client';
import { Box, Stack } from '@chakra-ui/react';
import { dinero } from 'dinero.js';
import { useRouter } from 'next/router';
import * as React from 'react';

import { AccordionMembershipCard } from '@/components/AccordionMembershipCard';
import { MembershipCtaCard } from '@/components/MembershipCtaCard';
import { UnassignedMembershipInvoicesAlert } from '@/components/UnassignedMembershipInvoicesAlert';
import { useDefaultCurrency } from '@/hooks/useDefaultCurrency';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { Hummus, HummusAside, HummusMain } from '@/layouts/Hummus';

import { MembershipPage_CurrentUser } from './graphql-types/MembershipPage_CurrentUser';

const GET_USER = gql`
  query MembershipPage_CurrentUser(
    $relevantMembershipTermsInput: RelevantMembershipTermsInput
  ) {
    currentUser {
      _id
      firstName
      displayName
      canCurrentUserAddPaymentMethod
      membershipData {
        ...MembershipCtaCard_MembershipDataFragment
        ...AccordionMembershipCard_MembershipDataFragment
      }
      creditBalance
      rosterOrganization {
        _id
        currentUserOrganizationPermission {
          canManageOrganizationProfile
        }
      }
    }
  }
  ${AccordionMembershipCard.membershipDataFragment}
  ${MembershipCtaCard.membershipDataFragment}
`;

export const MembershipPage: React.FC = () => {
  const router = useRouter();
  const { error, loading, data, refetch } =
    useQuery<MembershipPage_CurrentUser>(GET_USER, {
      // Use cache-and-network to ensure fresh data is fetched
      // This is important when users navigate here after completing payments
      fetchPolicy: 'cache-and-network',
      // Notify on network status change to show loading states during refetch
      notifyOnNetworkStatusChange: true,
    });
  const { currency } = useDefaultCurrency();

  // Refetch data when component mounts or when coming from order history
  React.useEffect(() => {
    const shouldRefresh = 
      // Check if coming from order history via query parameter
      router.query.refresh === 'true' ||
      // Check if referrer is order history (fallback)
      (typeof window !== 'undefined' && 
       (window.document.referrer.includes('/me/orders') || 
        window.document.referrer.includes('/order')));

    if (shouldRefresh) {
      // Force a network request to get fresh data
      refetch();
      
      // Clean up the refresh query parameter
      if (router.query.refresh) {
        const newQuery = { ...router.query };
        delete newQuery.refresh;
        router.replace({ pathname: router.pathname, query: newQuery }, undefined, { shallow: true });
      }
    }
  }, [router.query.refresh, refetch, router]);

  // Also refetch when component mounts to ensure fresh membership data
  React.useEffect(() => {
    // Only refetch if we have cached data to avoid double loading
    if (data && !loading) {
      refetch();
    }
  }, []); // Empty dependency array means this runs once on mount

  return (
    <Hummus>
      <HummusMain>
        {loading ? (
          <Loading />
        ) : error || !data?.currentUser ? (
          <UnexpectedError />
        ) : (
          data.currentUser.membershipData && (
            <Box>
            <UnassignedMembershipInvoicesAlert
              userId={data.currentUser._id}
            />
            <AccordionMembershipCard
              userId={data.currentUser._id}
              userName={data.currentUser.firstName}
              membershipData={data.currentUser.membershipData}
              displayName={data.currentUser.displayName}
              creditBalance={
                data.currentUser.creditBalance
                  ? dinero(data.currentUser.creditBalance)
                  : dinero({ amount: 0, currency })
              }
              canCurrentUserManageOrganizationProfile={
                data.currentUser.rosterOrganization
                  ?.currentUserOrganizationPermission
                  .canManageOrganizationProfile
              }
              variant="new"
            />
            </Box>
          )
        )}
      </HummusMain>
      <HummusAside>
        <Stack spacing="4">
          {data?.currentUser?.membershipData && (
            <MembershipCtaCard
              membershipData={data.currentUser.membershipData}
              userFirstName={data.currentUser.firstName}
              shouldBeMemberView
              newViewProfile={true}
            />
          )}
        </Stack>
      </HummusAside>
    </Hummus>
  );
};
